import { useState, useCallback } from "react";
import RevealSection from "@/components/base/RevealSection";
import { submitNetlifyForm } from "@/utils/netlifyForms";

export default function CorporateEnquiries() {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    const form = e.currentTarget;
    const formData = new FormData(form);
    try {
      await submitNetlifyForm(formData);
      setSubmitted(true);
      form.reset();
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please try again.");
    }
  }, []);

  return (
    <RevealSection>
      <section className="bg-white px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <div className="max-w-3xl mx-auto">
          <div className="max-w-2xl mx-auto text-center mb-10">
            <h1 className="text-3xl md:text-4xl lg:text-[42px] font-bold text-navy text-center mb-3 ca-page-title">
              Corporate Enquiries
            </h1>
            <p className="text-navy/60 text-base md:text-lg text-center mb-6">
              For workplace training, team licences, workshops or business enquiries, complete the form below.
            </p>
            <a
              href="https://www.linkedin.com/company/calm_authority/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-teal font-semibold hover:text-navy transition-colors"
              aria-label="Visit Calm Authority on LinkedIn"
            >
              <i className="ri-linkedin-box-fill text-2xl" />
              Follow Calm Authority on LinkedIn
            </a>
          </div>

          {submitted && (
            <div className="bg-seafoam/20 rounded-2xl p-8 md:p-12 text-center mb-10">
              <div className="w-16 h-16 rounded-full bg-teal/10 flex items-center justify-center mx-auto mb-6">
                <i className="ri-check-line text-teal text-3xl" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-navy mb-4 ca-page-title">
                Message Sent
              </h2>
              <p className="text-navy/70 text-base md:text-lg leading-relaxed mb-8">
                Thank you for reaching out. Someone will get back to you shortly.
              </p>
              <button
                type="button"
                onClick={() => setSubmitted(false)}
                className="inline-flex items-center justify-center bg-teal text-white font-semibold px-8 py-3.5 rounded-lg hover:bg-navy transition-all duration-300 text-base whitespace-nowrap"
              >
                Send Another Message
              </button>
            </div>
          )}

          {!submitted && (
            <div className="bg-white border border-seafoam/40 rounded-xl shadow-sm p-6 md:p-10">
              <h2 className="text-2xl md:text-3xl font-bold text-navy mb-3 ca-page-title">
                Corporate Enquiries
              </h2>
              <p className="text-navy/60 text-base mb-6">
                For workplace training, team licences, workshops or business enquiries, complete this form and Katia will follow up.
              </p>
              <form name="corporate_enquiry" method="POST" data-netlify="true" action="/thank-you" onSubmit={handleSubmit} className="flex flex-col gap-5">
                <input type="hidden" name="form-name" value="corporate_enquiry" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="corp_first_name" className="text-sm font-medium text-navy">First Name</label>
                    <input id="corp_first_name" name="first_name" type="text" required className="w-full border border-seafoam rounded-lg px-4 py-3 text-navy text-base focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent transition-all" placeholder="Jane" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="corp_last_name" className="text-sm font-medium text-navy">Last Name</label>
                    <input id="corp_last_name" name="last_name" type="text" required className="w-full border border-seafoam rounded-lg px-4 py-3 text-navy text-base focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent transition-all" placeholder="Doe" />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="corp_email" className="text-sm font-medium text-navy">Email</label>
                  <input id="corp_email" name="email" type="email" required className="w-full border border-seafoam rounded-lg px-4 py-3 text-navy text-base focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent transition-all" placeholder="jane@example.com" />
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="corp_phone" className="text-sm font-medium text-navy">Phone Number (including country code)</label>
                  <input id="corp_phone" name="phone" type="tel" required className="w-full border border-seafoam rounded-lg px-4 py-3 text-navy text-base focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent transition-all" placeholder="+34 684 14 51 63" />
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="corp_company" className="text-sm font-medium text-navy">Organisation / Company</label>
                  <input id="corp_company" name="company" type="text" required className="w-full border border-seafoam rounded-lg px-4 py-3 text-navy text-base focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent transition-all" placeholder="Organisation name" />
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="corp_message" className="text-sm font-medium text-navy">Message</label>
                  <textarea id="corp_message" name="message" required maxLength={700} rows={6} className="w-full border border-seafoam rounded-lg px-4 py-3 text-navy text-base focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent transition-all resize-none" placeholder="Tell us about your team, training need or enquiry." />
                  <p className="text-xs text-navy/40 text-right">Max 700 characters</p>
                </div>
                {error && <p className="text-sm text-red-600 text-center">{error}</p>}
                <button type="submit" className="w-full bg-teal text-white font-semibold py-4 rounded-lg hover:bg-navy transition-all duration-300 text-base whitespace-nowrap">Send Corporate Enquiry</button>
              </form>
            </div>
          )}
        </div>
      </section>
    </RevealSection>
  );
}
